/**
 * Operators Store
 *
 * Zustand store for operator state management.
 * Fetches operators from the API and maps to FullOperator type.
 */

import { create } from 'zustand';
import { operatorsAPI } from '@/services/api';
import {
  type FullOperator,
  type OperatorLifecycleStatus,
  type OperatorTrust,
  type OperatorDrivesType,
  type CapabilityLevel,
} from '@/components/operators';

/**
 * Map a backend operator API response to the rich FullOperator type.
 * Backend returns simpler data; we fill sensible defaults for UI-only fields.
 */
function mapApiOperator(op: any): FullOperator {
  // Derive team name from teamRef (format: team_{companyId}_{teamName} or just a name)
  const teamName = op.teamName || op.teamRef?.split('_').pop() || 'General';
  const teamId = op.teamId || op.teamRef || 'team-general';

  // Derive role from jobId or specialization
  const role = op.role || op.specialization || op.jobRef?.replace(/_/g, ' ') || 'Operator';

  // Default trust scores
  const trust: OperatorTrust = {
    internal: {
      score: 75,
      status: (op.status === 'retired' ? 'retired' : 'active') as OperatorLifecycleStatus,
      history: [],
    },
    external: {
      score: 50,
      status: (op.status === 'retired' ? 'retired' : 'active') as OperatorLifecycleStatus,
      history: [],
    },
  };

  // Derive capabilities from job type
  const capabilities: Record<string, CapabilityLevel> = {};
  const jobLower = (op.jobRef || op.role || '').toLowerCase();
  if (jobLower.includes('backend') || jobLower.includes('engineer')) {
    capabilities['Code Review'] = 'expert';
    capabilities['API Design'] = 'proficient';
    capabilities['Testing'] = 'proficient';
  } else if (jobLower.includes('frontend')) {
    capabilities['UI Development'] = 'expert';
    capabilities['Accessibility'] = 'proficient';
    capabilities['Testing'] = 'proficient';
  } else if (jobLower.includes('market') || jobLower.includes('content')) {
    capabilities['Content Writing'] = 'expert';
    capabilities['SEO'] = 'proficient';
    capabilities['Social Media'] = 'proficient';
  } else {
    capabilities['General'] = 'proficient';
  }

  // Default drives
  const drives: OperatorDrivesType = {
    detail_oriented: 0.7,
    fast_paced: 0.5,
    collaborative: 0.6,
    independent: 0.5,
    cautious: 0.5,
    innovative: 0.6,
  };

  // Map boundaries
  const boundaries = {
    allowed: op.boundaries?.canModify || [],
    forbidden: op.boundaries?.cannotModify || [],
    requiresApproval: op.boundaries?.requiresApproval || [],
  };

  return {
    id: op.operatorId || op.id,
    name: op.name,
    role,
    teamId,
    teamName,
    jobRef: op.jobRef,
    trust,
    autoGraduationBlocked: false,
    capabilities,
    boundaries,
    drives,
    currentTask: op.currentTask,
    tasksCompleted: op.tasksCompleted || 0,
    createdAt: op.createdAt || new Date().toISOString(),
    provider: op.provider || 'Claude',
  };
}

interface OperatorsStore {
  operators: FullOperator[];
  loading: boolean;
  error: string | null;

  // Fetch operators from API
  fetchOperators: () => Promise<void>;

  // Create a new operator via API, then refresh the list
  createOperator: (data: { jobId: string; teamId: string; name?: string }) => Promise<boolean>;

  // Get a single operator by ID or name
  getOperator: (idOrName: string) => FullOperator | undefined;

  // Check if operator is retired
  isRetired: (idOrName: string) => boolean;

  // Retire an operator via API + update local state
  retireOperator: (id: string) => Promise<void>;

  // Start an operator via API
  startOperator: (id: string) => Promise<void>;

  // Stop an operator via API
  stopOperator: (id: string) => Promise<void>;

  // Update operator trust status for a specific dimension
  updateTrustStatus: (id: string, dimension: 'internal' | 'external', status: OperatorLifecycleStatus) => void;

  // Fetch drive state + trust from Lucian layer for a specific operator
  fetchOperatorDrives: (id: string) => Promise<void>;
}

export const useOperatorsStore = create<OperatorsStore>((set, get) => ({
  operators: [],
  loading: false,
  error: null,

  fetchOperators: async () => {
    set({ loading: true, error: null });
    try {
      const data = await operatorsAPI.list();
      const operators = (data || []).map(mapApiOperator);
      set({ operators, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  createOperator: async (data) => {
    try {
      await operatorsAPI.create(data);
      // Refresh operator list after creation
      await get().fetchOperators();
      return true;
    } catch (err) {
      set({ error: (err as Error).message });
      return false;
    }
  },

  getOperator: (idOrName: string) => {
    const { operators } = get();
    return operators.find(op => op.id === idOrName || op.name === idOrName);
  },

  isRetired: (idOrName: string) => {
    const op = get().getOperator(idOrName);
    return op?.trust.internal.status === 'retired' || op?.trust.external.status === 'retired';
  },

  retireOperator: async (id: string) => {
    try {
      await operatorsAPI.update(id, { status: 'retired' });
      set((state) => ({
        operators: state.operators.map(op =>
          op.id === id
            ? {
                ...op,
                trust: {
                  internal: { ...op.trust.internal, status: 'retired' as OperatorLifecycleStatus },
                  external: { ...op.trust.external, status: 'retired' as OperatorLifecycleStatus },
                }
              }
            : op
        )
      }));
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  startOperator: async (id: string) => {
    try {
      await operatorsAPI.start(id);
      await get().fetchOperators();
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  stopOperator: async (id: string) => {
    try {
      await operatorsAPI.stop(id);
      await get().fetchOperators();
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  updateTrustStatus: (id: string, dimension: 'internal' | 'external', status: OperatorLifecycleStatus) => {
    set((state) => ({
      operators: state.operators.map(op =>
        op.id === id
          ? {
              ...op,
              trust: {
                ...op.trust,
                [dimension]: { ...op.trust[dimension], status },
              }
            }
          : op
      )
    }));
  },

  fetchOperatorDrives: async (id: string) => {
    try {
      const data = await operatorsAPI.getDrives(id);
      if (!data) return;

      set((state) => ({
        operators: state.operators.map(op => {
          if (op.id !== id) return op;

          // Map Lucian 8-drive state to UI drives
          const driveState = (data.state as any)?.drives as Record<string, number> | undefined;
          const drives: OperatorDrivesType = driveState
            ? {
                detail_oriented: driveState.pattern ?? driveState.truth ?? op.drives.detail_oriented,
                fast_paced: driveState.optimization ?? op.drives.fast_paced,
                collaborative: driveState.connection ?? op.drives.collaborative,
                independent: driveState.mastery ?? op.drives.independent,
                cautious: driveState.protection ?? op.drives.cautious,
                innovative: driveState.curiosity ?? driveState.creation ?? op.drives.innovative,
              }
            : op.drives;

          // Update trust from backend
          const trust: OperatorTrust = data.trust
            ? {
                internal: {
                  score: data.trust.internal.score,
                  status: (data.trust.internal.status || op.trust.internal.status) as OperatorLifecycleStatus,
                  history: op.trust.internal.history,
                },
                external: {
                  score: data.trust.external.score,
                  status: op.trust.external.status,
                  history: op.trust.external.history,
                },
              }
            : op.trust;

          return { ...op, drives, trust };
        }),
      }));
    } catch {
      // Silently fail — drives endpoint may not be available (in-memory provider)
    }
  },
}));

/**
 * Get operator by ID or name (non-hook version for use outside components)
 */
export function getOperatorById(idOrName: string): FullOperator | undefined {
  return useOperatorsStore.getState().getOperator(idOrName);
}

/**
 * Check if operator is retired (non-hook version)
 */
export function isOperatorRetired(idOrName: string): boolean {
  return useOperatorsStore.getState().isRetired(idOrName);
}
